/**
 * A RUNNING scrape, read for a person: is it slow, or is it stuck? (EST-207, Oct 2026)
 *
 * Lee, 11 Oct 2026, reported a scrape as "stuck". It was not: it was inside the second of
 * fifteen searches, which had 1,922 listings at about fourteen seconds each, most of them homes
 * we already hold. The row had read "1 of 15" for six hours, with a bar at 88% that was the
 * listings found SO FAR and not the scrape; and nothing on the page said when the scraper had
 * last written. So the three things a person needs are worked out here, apart from the page,
 * so that they can be tested:
 *
 *  1. WHICH SEARCH is in hand, of how many; and a bar that goes by searches FINISHED, the only
 *     whole the job's record holds.
 *  2. THE LISTINGS, in words: processed of found SO FAR, saved, already held, skipped, failed.
 *  3. THE LAST WRITE: how long ago the scraper last touched the job, and "stuck" in words only
 *     when that is old by the fleet's own rule.
 *     ⚠️ It is the scraper's HEARTBEAT, not its progress. A scraper that is alive and getting
 *     nowhere (going round inside one step) keeps a fresh heartbeat. So lines 2 and 3 answer two
 *     different questions, and the row says so: alive is the last write; getting on is the
 *     listings line moving. Nothing here tries to detect "alive and getting nowhere".
 *
 * WHAT THE JOB'S RECORD HOLDS (the Scraper session, 11 Oct 2026; its firebase/claimRules.js):
 *  - `heartbeat`: top level, a Timestamp, written by a timer of its OWN every TEN SECONDS for
 *    as long as the scraper has the job in hand (its worker.js), from the first page of results
 *    to the job's end. It does not wait for a listing: while a long search is being LISTED the
 *    last write keeps moving and the counts below do not, which reads rightly as alive, listing.
 *    (The counts are written every five listings, and at each search's start and end.)
 *  - `progress.currentPostcodeIndex` (0-based: 1 on the second search), `progress.currentPostcode`,
 *    `progress.processedPostcodes` (searches FINISHED: 1 on the second search), `progress.totalPostcodes`.
 *  - `progress.processedProperties` of `progress.totalProperties`.
 *    ⚠️ CUMULATIVE over the searches LISTED SO FAR. The record does not hold each search's own
 *    count, and the total GROWS as each new search is listed, so the fraction can go DOWN
 *    between searches. Hence "so far", nothing subtracted, and never "left in this search".
 *  - `progress.successfulProperties` (saved), `existingProperties` (already held, so skipped),
 *    `skippedProperties` (skipped for another reason), `failedProperties`.
 *  - ⚠️ A RESUMED job (`resumeReason` is set: an orphaned job, or one handed back): the total is
 *    not refreshed and the processed count starts again at nought, so it can read "18 of 4".
 *    Only the saved count is right throughout. No fraction, and no bar, is made from those.
 *    ⚠️ A FOLLOW-UP IS OWED when the Scraper's fix (EST-205) is on the boxes: a record will then
 *    carry `progress.currentSearchTotal` and `progress.processedBeforeSearch`, its two counts
 *    will be whole after a resume, and "its counts start again from there" will no longer be
 *    true of it. Nothing here reads those fields yet: they exist on no box.
 *  - THE FLEET'S OWN RULE: a `running` job whose heartbeat is older than TEN MINUTES may be
 *    claimed by any other scraper. That is the line for the word "stuck"; it is not a guess.
 *
 * ⚠️ THE CLOCK. The heartbeat is the server's time and "now" is this browser's. A browser whose
 * clock is wrong gives an age that is negative or absurd: that is "cannot tell", NEVER "stuck".
 *
 * Pure: no clock is read here. The caller says what now is.
 */

/** No write for this long, on a job that says running: said in amber. The scraper writes every ten seconds, so five minutes is thirty writes missed. */
export const QUIET_AFTER_MS = 5 * 60 * 1000;
/** The fleet's take-over line: a running job with no heartbeat for this long may be claimed by another scraper. */
export const TAKE_OVER_AFTER_MS = 10 * 60 * 1000;
/** A last write that reads as older than this is more likely this browser's clock than the scrape. */
export const ABSURD_AFTER_MS = 24 * 60 * 60 * 1000;
/** The browser's clock may be this far behind the server's before the age is called "cannot tell". */
export const CLOCK_SLACK_MS = 60 * 1000;

const n = (v) => Number(v || 0).toLocaleString("en-GB");
const count = (v) => (typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : null);
/** The first of these that is a count; else null. A field that is there and is not a count is not read as nought. */
const firstCount = (...values) => { for (const v of values) { const c = count(v); if (c !== null) return c; } return null; };
/** The first that is a count ABOVE nought. The Scrape model gives a record with no progress a total of 0, which is "not said", not "none". */
const firstPositive = (...values) => { for (const v of values) { const c = count(v); if (c !== null && c > 0) return c; } return null; };

/** A time as the database, the model or a test may hold it, in milliseconds; null when it is not a time. */
export function msOf(t) {
  if (t === null || t === undefined) return null;
  if (typeof t === "number") return Number.isFinite(t) ? t : null;
  if (t instanceof Date) return Number.isNaN(t.getTime()) ? null : t.getTime();
  if (typeof t.toMillis === "function") { const ms = t.toMillis(); return Number.isFinite(ms) ? ms : null; }
  if (typeof t.seconds === "number") return t.seconds * 1000 + Math.floor((t.nanoseconds || 0) / 1e6);
  if (typeof t._seconds === "number") return t._seconds * 1000 + Math.floor((t._nanoseconds || 0) / 1e6);
  if (typeof t === "string" && t.trim() !== "") { const ms = Date.parse(t); return Number.isNaN(ms) ? null : ms; }
  return null;
}

/** "40 s", "3 min", "2 h 5 min": how long, for a person. */
export function agoWords(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 5) return "a moment";
  if (s < 120) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 120) return `${m} min`;
  const h = Math.floor(m / 60);
  return `${h} h ${m % 60} min`;
}

/**
 * Which search is in hand, and how many are finished.
 * @returns {{ several: boolean, total: number|null, finished: number|null, inHand: number|null, postcode: string|null, words: string|null }}
 */
export function searchReading(scrape) {
  const s = scrape || {}; const p = s.progress || {};
  const listed = Array.isArray(s.postcodes) ? s.postcodes.length : Array.isArray(s.searches) ? s.searches.length : null;
  const total = firstPositive(p.totalPostcodes, s.totalPostcodes, listed);
  const index = firstCount(p.currentPostcodeIndex, s.currentPostcodeIndex);
  const finishedSaid = firstCount(p.processedPostcodes, s.processedPostcodes);
  // searches finished: said by the record; else the search in hand is the one after the finished ones
  const finishedRaw = finishedSaid !== null ? finishedSaid : index;
  const finished = finishedRaw === null ? null : total !== null ? Math.min(finishedRaw, total) : finishedRaw;
  const inHandRaw = index !== null ? index + 1 : finished !== null ? finished + 1 : null;
  const inHand = inHandRaw === null ? null : total !== null ? Math.min(inHandRaw, total) : inHandRaw;
  const postcodeRaw = p.currentPostcode ?? s.currentPostcode;
  const postcode = typeof postcodeRaw === "string" && postcodeRaw.trim() !== "" ? postcodeRaw.trim() : null;
  const several = total !== null && total > 1;
  const words = several && inHand !== null ? `Search ${n(inHand)} of ${n(total)}${postcode ? ` · ${postcode}` : ""}` : null;
  return { several, total, finished, inHand, postcode, words };
}

/**
 * The listings, in words. Never a fraction over one; never a fraction at all for a resumed job.
 * @returns {{ processed, total, saved, held, skipped, failed, resumed: boolean, fraction: number|null, words: string }}
 */
export function listingsReading(scrape) {
  const s = scrape || {}; const p = s.progress || {};
  const saved = firstCount(p.successfulProperties, s.successfulProperties) || 0;
  const held = firstCount(p.existingProperties, s.existingProperties) || 0;
  const skipped = firstCount(p.skippedProperties, s.skippedProperties) || 0;
  const failed = firstCount(p.failedProperties, s.failedProperties) || 0;
  const said = firstCount(p.processedProperties);
  // what the scraper says it has processed; an older record does not say, and then it is the sum of what became of them
  const processed = said !== null ? said : saved + held + skipped + failed;
  const total = firstCount(p.totalProperties, s.totalProperties);
  const resumed = Boolean(s.resumeReason) || (total !== null && total > 0 && processed > total);
  const parts = [];
  let fraction = null;
  if (resumed) {
    parts.push(`${n(processed)} ${processed === 1 ? "listing" : "listings"} since it was resumed`);
  } else if (total !== null && total > 0) {
    fraction = processed / total;
    parts.push(`${n(processed)} of ${n(total)} listings so far`);
  } else {
    parts.push(`${n(processed)} ${processed === 1 ? "listing" : "listings"} so far`);
  }
  parts.push(`${n(saved)} saved`);
  if (held) parts.push(`${n(held)} already held`);
  if (skipped) parts.push(`${n(skipped)} skipped`);
  if (failed) parts.push(`${n(failed)} failed`);
  const note = resumed
    ? ` This scrape was resumed: its counts start again from there${total !== null && total > 0 ? `, and the total recorded (${n(total)}) is from before` : ""}. The saved figure is right throughout.`
    : "";
  return { processed, total, saved, held, skipped, failed, resumed, fraction, words: `${parts.join(" · ")}.${note}` };
}

/**
 * The bar: by searches FINISHED for a scrape of several searches; by listings for a scrape of one.
 * Null when there is no honest bar to draw (a resumed job of one search; nothing to go by).
 * @returns {null | { by: 'searches'|'listings', percent: number, label: string }}
 */
export function barReading(scrape) {
  const search = searchReading(scrape);
  if (search.several) {
    if (search.finished === null) return null;
    const percent = Math.max(0, Math.min(100, (search.finished / search.total) * 100));
    return { by: "searches", percent, label: `${n(search.finished)} of ${n(search.total)} searches finished` };
  }
  const listings = listingsReading(scrape);
  if (listings.fraction === null) return null;
  const percent = Math.max(0, Math.min(100, listings.fraction * 100));
  return { by: "listings", percent, label: `${Math.round(percent)}%` };
}

/**
 * When the scraper last wrote to the job, and what that means.
 *  - `none`: the job does not say it is running: nothing is said.
 *  - `fresh`: a write in the last five minutes.
 *  - `quiet`: no write for five to ten minutes. Amber.
 *  - `stuck`: no write for ten minutes or more: the fleet's own take-over line.
 *  - `cannot-tell`: no heartbeat on the record, or an age this browser's clock makes negative or absurd.
 * @param {number} nowMs this browser's time now
 * @returns {{ state: 'none'|'fresh'|'quiet'|'stuck'|'cannot-tell', ageMs: number|null, words: string|null }}
 */
export function touchReading(scrape, nowMs) {
  const s = scrape || {};
  if (s.status !== "running") return { state: "none", ageMs: null, words: null };
  const beat = msOf(s.heartbeat);
  if (beat === null) return { state: "cannot-tell", ageMs: null, words: "No last write is recorded for this scrape: whether it is working cannot be told here." };
  const now = msOf(nowMs);
  if (now === null) return { state: "cannot-tell", ageMs: null, words: "The time now could not be read: how long ago the scraper last wrote cannot be told." };
  const age = now - beat;
  if (age < -CLOCK_SLACK_MS) return { state: "cannot-tell", ageMs: age, words: "The scraper's last write is in this browser's future, so this browser's clock is wrong: how long ago it wrote cannot be told." };
  if (age > ABSURD_AFTER_MS) return { state: "cannot-tell", ageMs: age, words: "The last write reads as more than a day ago. Either this browser's clock is wrong, or no scraper has touched this scrape since: see the Scrapers tab." };
  const ago = Math.max(0, age);
  if (ago < QUIET_AFTER_MS) return { state: "fresh", ageMs: ago, words: `Last write ${agoWords(ago)} ago: the scraper is alive. Whether it is getting on is the listings line moving.` };
  const minutes = Math.floor(ago / 60000);
  if (ago < TAKE_OVER_AFTER_MS) return { state: "quiet", ageMs: ago, words: `No write for ${n(minutes)} minutes (the scraper writes every ten seconds while it has the job).` };
  return { state: "stuck", ageMs: ago, words: `No write for ${agoWords(ago)}: this looks stuck. Another scraper may take over a scrape with no write for 10 minutes.` };
}

/**
 * Is this scrape drawn as under way? A RUNNING job always is, whatever it has found: a job that hangs before its
 * first listing is the one a person most needs to see has stopped writing. A job only CLAIMED is, once it has
 * listings (as before).
 */
export function showsProgress(scrape) {
  const s = scrape || {};
  if (s.status === "running") return true;
  return s.status === "claimed" && (firstCount((s.progress || {}).totalProperties, s.totalProperties) || 0) > 0;
}

/** Everything the row says of a scrape that is under way. */
export function runningReading(scrape, nowMs) {
  return { search: searchReading(scrape), bar: barReading(scrape), listings: listingsReading(scrape), touch: touchReading(scrape, nowMs) };
}
